import { Box, Button, Container, Flex, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import { useState, useMemo, useRef } from "react";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import { report_api } from "./report.service";
import { showErrorToast, showSuccessToast } from "utilities/Toast";
import { IconDownload, IconPrinter } from "@tabler/icons-react";
import { invoice_api } from "pages/transactions/invoice/invoice.service";

export default function HoldStockReport() {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const printRef = useRef();

  // ================= FETCH ================= //
 const fetchReport = async () => {
  if (!startDate && !endDate) {
    showErrorToast({ title: "Error", message: "Select at least one date" });
    return;
  }

  const finalStart = startDate || endDate;
  const finalEnd = endDate || startDate;

  setLoading(true);

  try {
    const response = await report_api.get_hold_stock_report({
      start_date: moment(finalStart).format("YYYY-MM-DD"),
      end_date: moment(finalEnd).format("YYYY-MM-DD"),
    });

    if (!response.success) {
      setTableData([]);
      return;
    }

    setTableData(response.data || []);
  } catch (err) {
    console.error(err);
  } finally {
    setLoading(false);
  }
};
  // ================= TABLE ================= //
  const columns = useMemo(() => [
    { header: "Invoice No", accessorKey: "invoice_no" },
    {
      header: "Date",
      accessorKey: "invoice_date",
      Cell: ({ cell }) => moment(cell.getValue()).format("DD-MM-YYYY"),
    },
    { header: "Product Name", accessorKey: "product_name" },
    { header: "Counter", accessorKey: "counter_name" },
    {
      header: "Hold Stock",
      accessorKey: "hold_stock",
      Cell: ({ cell }) => Number(cell.getValue()).toFixed(3),
    },
    {
      header: "Original Stock",
      accessorKey: "original_stock",
      Cell: ({ cell }) => Number(cell.getValue()).toFixed(3),
    },
  ], []);

  const table = useMantineReactTable({
    columns,
    data: tableData,
    state: { isLoading: loading },
    enableStickyHeader: true,
    mantineTableContainerProps: {
      style: { maxHeight: "400px", overflowY: "auto" },
    },
  });

  // ================= CSV ================= //
  const downloadCSV = () => {
    if (tableData.length === 0) {
      showErrorToast({ title: "Error", message: "No data" });
      return;
    }

    const csvRows = [
      ["Hold Stock Report"],
      [`Start Date: ${moment(startDate).format("DD/MM/YYYY")}`],
      [`End Date: ${moment(endDate).format("DD/MM/YYYY")}`],
      [],
      ["Invoice No", "Date", "Product", "Counter", "Hold Stock", "Original Stock"],
      ...tableData.map(r => [
        r.invoice_no,
        moment(r.invoice_date).format("DD-MM-YYYY"),
        r.product_name,
        r.counter_name,
        r.hold_stock,
        r.original_stock,
      ]),
    ];

    const csvContent =
      "data:text/csv;charset=utf-8," +
      csvRows.map(e => e.join(",")).join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = `HoldStock_${moment().format("DDMMYYYY")}.csv`;
    link.click();

    showSuccessToast({ title: "Success", message: "CSV Downloaded" });
  };

  // ================= PRINT ================= //
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  const PrintComponent = () => (
    <div ref={printRef} style={{ padding: 20 }}>
      <h2 style={{ textAlign: "center" }}>Hold Stock Report</h2>

      <table border="1" style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th>Invoice No</th>
            <th>Date</th>
            <th>Product</th>
            <th>Counter</th>
            <th>Hold Stock</th>
            <th>Original Stock</th>
          </tr>
        </thead>

        <tbody>
          {tableData.map((r, i) => (
            <tr key={i}>
              <td>{r.invoice_no}</td>
              <td>{moment(r.invoice_date).format("DD-MM-YYYY")}</td>
              <td>{r.product_name}</td>
              <td>{r.counter_name}</td>
              <td>{Number(r.hold_stock).toFixed(3)}</td>
              <td>{Number(r.original_stock).toFixed(3)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  const updateStockReference = async () => {
  try {
    const res = await invoice_api.update_stock_reference();

    if (res.headers["content-type"].includes("application/json")) {
  const text = await res.data.text();
  const data = JSON.parse(text);

  showErrorToast({
    title: "Error",
    message: data.message,
  });
  return;
}

    // Create download link
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", "updated_invoice_stock_report.csv");

    document.body.appendChild(link);
    link.click();
    link.remove();

    showSuccessToast({
      title: "Success",
      message: "Stock updated & CSV downloaded",
    });

    

  } catch (err) {
    showErrorToast({
      title: "Error",
      message: err.message,
    });
  }
};

  // ================= UI ================= //
  return (
    <Container fluid>
      <div style={{ display: "none" }}>
        <PrintComponent />
      </div>

      <Box p={25}>
        <Flex justify="space-between" align="end" wrap="wrap">
          <Text size="lg" fw={600}>Hold Stock Report</Text>

          <Flex gap={15} align="end">

            <Button
              onClick={() => {
                updateStockReference();
              }}
            >
              Update Stock Reference
            </Button>

            <Button leftIcon={<IconDownload />} onClick={downloadCSV}>
              CSV
            </Button>

            <Button leftIcon={<IconPrinter />} onClick={handlePrint}>
              Print
            </Button>

            <DatePickerInput
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              style={{ width: "200px" }}
            />

            <DatePickerInput
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              disabled={!startDate}
              style={{ width: "200px" }}
            />

            <Button color="green" onClick={fetchReport}>
              Search
            </Button>
          </Flex>
        </Flex>
      </Box>

      <Box px={25}>
        <MantineReactTable table={table} />
      </Box>
    </Container>
  );
}